/**
 * Copy-to-clipboard control for a guideline's full text.
 *
 * Used on the Review Summary rows and the guideline "blue box" so a reviewer
 * can copy a Met guideline's text (without its title) into the BO Notes with a
 * single click. After a successful copy the control briefly shows a check
 * and "Copied" before reverting to "Copy".
 * @module GuidelineCopyButton
 */
import React, { useEffect, useRef, useState } from "react";
import { CheckIcon } from "../../../svg/check-icon";
import { CopyIcon } from "../../../svg/copy-icon";
import { logError } from "../../../utils/customLogger";

/** Props for {@link GuidelineCopyButton}. */
export interface GuidelineCopyButtonProps {
  /** Text written to the clipboard (the full guideline text, no title). */
  text: string;
  /** Accessible label and tooltip, e.g. "Copy guideline text". */
  label: string;
  /** Fired after the text is successfully copied to the clipboard. */
  onCopy?: () => void;
}

/** How long the "Copied" confirmation stays visible, in milliseconds. */
const COPIED_TIMEOUT_MS = 2000;

/**
 * Renders a borderless Copy button that copies `text` to the clipboard and
 * briefly confirms with a "Copied" check before reverting.
 *
 * @param props - Text to copy and the accessible label
 * @returns The rendered copy control
 */
export const GuidelineCopyButton: React.FC<GuidelineCopyButtonProps> = ({
  text,
  label,
  onCopy,
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending revert timer on unmount.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(
        () => setCopied(false),
        COPIED_TIMEOUT_MS,
      );
    } catch (error) {
      logError(error as Error, "Error while copying guideline text");
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      aria-label={copied ? "Copied" : label}
      title={copied ? "Copied" : label}
      className="inline-flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent p-1 px-2 text-[10px] font-normal leading-4 text-[#0F0F0F] transition-colors [font-family:Lato] [font-feature-settings:'ss01'_on] [font-kerning:none] hover:bg-[#F5F5F5]"
    >
      {copied ? (
        <>
          <CheckIcon width="12" height="12" color="#008F32" />
          Copied
        </>
      ) : (
        <>
          <CopyIcon width="12" height="12" stroke="#000000" />
          Copy
        </>
      )}
    </button>
  );
};
